import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js'

import { SimCall } from '@/native/simCall'
import { reportDeviceResult, uploadDeviceLog } from '@/services/api/calling.service'
import { logger } from '@/utils/logger'

import { crmCallLogExtras, freshPending, matchPendingCalls, syncWindowStart, type LogEntry } from './matchCallLog'
import { simStorage } from './simStorage'

let syncing: Promise<void> | null = null

function toE164(number: string, region: string): string | null {
  const parsed = parsePhoneNumberFromString(number, region as CountryCode)
  return parsed?.isValid() ? parsed.number : null
}

/**
 * Reads the phone's call log and reports what the CRM needs: the result of each SIM call the app
 * placed, plus other calls with numbers the CRM called before (callbacks, missed calls). Runs on
 * app start, when the app returns to the foreground (the dialer usually hands back) and on demand.
 * Concurrent runs share one sync.
 */
export function syncCallLog(defaultRegion: string): Promise<void> {
  syncing ??= runSync(defaultRegion).finally(() => {
    syncing = null
  })
  return syncing
}

async function runSync(defaultRegion: string) {
  const deviceId = await simStorage.deviceId()
  if (!deviceId) return
  const permissions = await SimCall.checkPermissions()
  if (permissions.callLog !== 'granted') return

  const now = Date.now()
  const pending = freshPending(await simStorage.pending(), now)
  const since = syncWindowStart(pending, await simStorage.lastSyncAt(), now)
  const workSim = await simStorage.workSim()
  const { entries: rows } = await SimCall.queryCallLog({ sinceMillis: since, phoneAccountId: workSim ?? undefined })
  const entries: LogEntry[] = rows.map((row) => ({
    id: row.id,
    e164: toE164(row.number, defaultRegion),
    type: row.type,
    date: row.date,
    durationSec: row.durationSec,
    phoneAccountId: row.phoneAccountId,
  }))

  const { matches, unmatched } = matchPendingCalls(pending, entries)
  const reported: string[] = []
  for (const { pending: call, entry } of matches) {
    try {
      await reportDeviceResult(call.callId, {
        deviceId,
        callLogId: entry.id,
        durationSec: entry.durationSec,
        startedAt: new Date(entry.date).toISOString(),
        endedAt: new Date(entry.date + entry.durationSec * 1000).toISOString(),
        simAccountId: entry.phoneAccountId,
      })
      reported.push(entry.id)
    } catch (error) {
      logger.warn('Could not report a SIM call result; will retry on next sync', error)
      unmatched.push(call)
    }
  }
  await simStorage.setPending(unmatched)
  await simStorage.addSentLogIds(reported)

  // Entries matched to a pending call are never extras, even when reporting them failed: they are
  // retried as that call's result on the next sync.
  const exclude = new Set([...(await simStorage.sentLogIds()), ...matches.map((m) => m.entry.id)])
  const extras = crmCallLogExtras(entries, await simStorage.crmNumbers(), exclude)
  if (extras.length > 0) {
    await uploadDeviceLog(
      deviceId,
      extras.slice(0, 200).map((e) => ({
        callLogId: e.id,
        number: e.e164 ?? '',
        type: e.type === 'OTHER' ? 'INCOMING' : e.type,
        startedAt: new Date(e.date).toISOString(),
        durationSec: e.durationSec,
        simAccountId: e.phoneAccountId,
      }))
    )
    await simStorage.addSentLogIds(extras.map((e) => e.id))
  }
  await simStorage.setLastSyncAt(now)
}
